"use client";

import { Plug } from "lucide-react";
import {
  CrossSellBand,
  DevLandingShell,
  FinalCta,
  GetKeyButton,
  HeroBadge,
  LiveDot,
  SecondaryCta,
  SectionHeading,
  StatsRow,
  metalText,
} from "@/components/marketing/dev-landing/chrome";
import { FeatureSplit, TextFeatureList } from "@/components/marketing/dev-landing/sections";
import { FlowDriftMock } from "@/components/marketing/dev-landing/mocks";
import { ChatDemo, Hi } from "@/components/marketing/dev-landing/chat-demo";
import {
  ClaudeConnectSteps,
  McpVideoPlaceholder,
  WorksWithRow,
} from "@/components/marketing/dev-landing/mcp-connect";
import { ToolCatalog } from "@/components/marketing/dev-landing/tool-catalog";

export default function OptionsFlowsMcpLandingClient({ keyHref }: { keyHref: string }) {
  return (
    <DevLandingShell>
      {/* Hero */}
      <div className="text-center">
        <HeroBadge>
          <LiveDot />
          <span className="text-sm text-[#d4d5d5]">Real-time options flow &middot; over MCP</span>
        </HeroBadge>

        <h1 className="mx-auto max-w-4xl text-balance text-4xl tracking-tight text-white sm:text-5xl md:text-6xl">
          Ask your AI what&apos;s <span style={metalText}>sweeping</span> right now
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-balance text-lg leading-relaxed text-white/70 sm:text-xl">
          Real-time options flow as MCP tools: net call and put premium drift plus significant prints -
          sweeps, blocks and splits - answered live in Claude, ChatGPT or Cursor.
        </p>

        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <GetKeyButton keyHref={keyHref} eventName="flows_mcp_lp_get_key_clicked" />
          <SecondaryCta href="#connect">
            <Plug className="h-4 w-4" />
            How to connect
          </SecondaryCta>
        </div>
        <p className="mt-4 text-xs text-white/50 sm:text-sm">Part of the Quant plan.</p>

        <WorksWithRow />
      </div>

      {/* Demo video */}
      <McpVideoPlaceholder
        youtubeEmbedUrl="https://www.youtube-nocookie.com/embed/Nq-TaiUqeS8?start=123"
        label="Watch Claude read NVDA's premium drift, flag the day's biggest sweeps and cross-check them against gamma walls through the QuantWheel MCP connector."
      />

      <StatsRow
        stats={[
          { value: "Live", label: "tape updates" },
          { value: "1m", label: "bar resolution" },
          { value: "<1min", label: "Claude setup" },
          { value: "Any", label: "US optionable ticker" },
        ]}
      />

      {/* Chat demo */}
      <div className="mt-20 sm:mt-24">
        <SectionHeading
          title={
            <>
              The tape, <span style={metalText}>summarized on demand</span>
            </>
          }
          sub="Flow dashboards show you everything; your AI tells you what matters. Same data, zero scanning."
        />
        <ChatDemo
          turns={[
            {
              question: "How's the premium drift in NVDA today - are calls or puts in control?",
              toolCalls: [{ tool: "get_flow_bars", args: "NVDA, 1m" }],
              answer: (
                <>
                  Calls are in control: net call premium is <Hi>+$14.2M</Hi> on the session versus{" "}
                  <Hi>-$3.1M</Hi> in puts, and the drift accelerated after 1:30 PM - the last hour added more
                  call premium than the entire morning.
                </>
              ),
            },
            {
              question: "Any single prints big enough to explain that?",
              toolCalls: [{ tool: "get_flow_trades", args: "NVDA" }],
              answer: (
                <>
                  Yes - a <Hi>$3.2M sweep</Hi> on the <Hi>08/22 190 calls</Hi> at the ask at 2:18 PM, followed
                  by two blocks totaling <Hi>$3.5M</Hi> on the 08/15 187.5s. That cluster is over 40% of the
                  day&apos;s net call premium.
                </>
              ),
            },
          ]}
        />
      </div>

      {/* Connect to Claude */}
      <ClaudeConnectSteps toolsHint="the flow toolset - premium drift bars and significant prints - alongside GEX and screeners" />

      {/* Tool catalog */}
      <div className="mt-20 sm:mt-24">
        <SectionHeading
          title="The flow toolset"
          sub="Two flow tools carry the story; the supporting cast gives your AI context to interpret it."
        />
        <ToolCatalog
          groups={[
            {
              title: "Flow",
              description: "Aggressor-signed premium, live off the tape.",
              tools: [
                { name: "get_flow_bars", blurb: "net call/put premium drift, 1m bars", tag: "Quant" },
                { name: "get_flow_trades", blurb: "sweeps, blocks and splits with side & size", tag: "Quant" },
              ],
            },
            {
              title: "Context",
              description: "So the AI can judge whether flow fights or follows positioning.",
              tools: [
                { name: "get_gex", blurb: "walls and flip for the same ticker" },
                { name: "get_stock_quote", blurb: "live spot price" },
                { name: "get_option_expirations", blurb: "expirations per symbol" },
                { name: "find_options_to_sell", blurb: "premium-selling candidates" },
              ],
            },
          ]}
        />
      </div>

      {/* Why */}
      <FeatureSplit
        eyebrow="Drift, not noise"
        title={
          <>
            The data behind <span style={metalText}>every answer</span>
          </>
        }
        lead="Aggressor-signed premium bucketed per minute answers the only question that matters: which side is real money leaning on? Your AI reads it straight off the tape."
        points={[
          { term: "get_flow_bars", text: "the session's drift, three numbers per minute" },
          { term: "get_flow_trades", text: "prints with type, side, strike and premium" },
          { term: "get_gex", text: "walls on the same server - flow + gamma in one prompt" },
        ]}
        visual={<FlowDriftMock />}
      />

      <div className="mt-20 sm:mt-24">
        <SectionHeading
          title={
            <>
              Flow reading, <span style={metalText}>without the grind</span>
            </>
          }
        />
        <TextFeatureList
          items={[
            {
              title: "Flow + GEX in one breath",
              description:
                "Because gamma tools live on the same server, one prompt can check whether a sweep is punching into a wall or riding a clean path.",
            },
            {
              title: "Session replays",
              description:
                "Ask about any past session and the AI replays that day's bars - post-mortem your trades with the actual tape.",
            },
          ]}
        />
      </div>

      <CrossSellBand
        badge="Prefer raw JSON?"
        title="The same flow feed, as a REST API"
        description="Premium drift bars and significant prints as compact endpoints for your own charts and bots - same key, same data."
        href="/options-flows-api"
        linkLabel="Explore the Flows API"
      />

      <CrossSellBand
        className="mt-5"
        badge="Want everything?"
        title="The full MCP server: GEX, screeners and your wheels"
        description="Flows is one of four tool surfaces - the overview page tours all 17 tools your connector unlocks."
        href="/mcp"
        linkLabel="See the full MCP"
      />

      <FinalCta
        title="Put a flow desk in your chat window"
        sub="Connect once and every conversation can read the tape - drift, sweeps and blocks - in real time."
        keyHref={keyHref}
        eventName="flows_mcp_lp_get_key_clicked"
        note="Options flow tools are part of the Quant plan."
      />
    </DevLandingShell>
  );
}
